import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import {
  Button,
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  Input,
  Indicators,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'ui'
import { toast } from 'react-hot-toast'
import { useCreateSchoolMutation, useDistricts } from '../queries'

// Validation schema
const schema = yup.object({
  school: yup.string().required('School name cannot be blank'),
  district: yup.string().required('District must be selected'),
  city: yup.string().optional(),
  overwriteId: yup.string().optional(),
})

export type NewSchoolFormValues = yup.InferType<typeof schema>

interface NewSchoolFormProps {
  onSuccess?: (data: any) => void
  onError?: (error: Error) => void
  hideTitle?: boolean
  hideSubmit?: boolean
  defaultValues?: Partial<NewSchoolFormValues>
  onChange?: (values: NewSchoolFormValues) => void
}

export const NewSchoolForm: React.FC<NewSchoolFormProps> = ({
  onSuccess,
  onError,
  hideTitle = false,
  hideSubmit = false,
  defaultValues,
  onChange,
}) => {
  // Fetch districts data
  const { data: districts, isLoading: isLoadingDistricts } = useDistricts()

  // Initialize the mutation
  const createSchoolMutation = useCreateSchoolMutation()

  // Store the current combo for onChange tracking
  const [currentCombo, setCurrentCombo] = useState('')

  // Initialize the form
  const form = useForm<NewSchoolFormValues>({
    defaultValues: defaultValues || {
      school: '',
      district: '',
      city: '',
      overwriteId: '',
    },
    resolver: yupResolver(schema),
    mode: 'onBlur',
  })

  // Update form when defaultValues change
  useEffect(() => {
    if (defaultValues && Object.keys(defaultValues).length > 0) {
      form.reset(defaultValues)
    }
  }, [defaultValues, form])

  // Watch for changes and notify parent component
  const watchedValues = form.watch()

  useEffect(() => {
    if (onChange) {
      const combo = `${watchedValues.school}${watchedValues.district}`
      if (combo !== currentCombo) {
        setCurrentCombo(combo)
        onChange(watchedValues)
      }
    }
  }, [watchedValues, onChange, currentCombo])

  // Handle district selection - update city field automatically
  const handleDistrictChange = (districtName: string) => {
    // Find the district to get its city
    const selectedDistrict = districts?.find((d) => d.name === districtName)

    if (selectedDistrict?.city?.name) {
      form.setValue('city', selectedDistrict.city.name, {
        shouldValidate: true,
      })
    } else {
      form.setValue('city', '', { shouldValidate: true })
    }

    return districtName
  }

  const handleSubmit = form.handleSubmit(async (data) => {
    try {
      // Show loading toast
      const toastId = toast.loading('Adding new school...')

      // Call the mutation
      const result = await createSchoolMutation.mutateAsync({
        school: data.school,
        district: data.district,
        city: data.city,
        overwriteId: data.overwriteId,
      })

      // Handle success
      toast.success('School added successfully', { id: toastId })
      form.reset()

      // Call onSuccess callback if provided
      if (onSuccess) {
        onSuccess(result)
      }
    } catch (error) {
      // Handle error
      toast.error('Failed to add school', {
        id: toast.loading(''), // Create and immediately dismiss a loading toast
      })

      // Call onError callback if provided
      if (onError && error instanceof Error) {
        onError(error)
      }
    }
  })

  return (
    <div className="flex flex-col w-full mb-6">
      {!hideTitle && (
        <article className="mb-2">
          <h2 className="text-xl font-semibold">Add a New School</h2>
        </article>
      )}

      <Form {...form}>
        <form onSubmit={handleSubmit} className="flex flex-col space-y-4">
          <FormField
            control={form.control}
            name="school"
            render={({ field }) => (
              <FormItem>
                <FormLabel>School Name</FormLabel>
                <FormControl>
                  <Input placeholder="Enter school name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="district"
            render={({ field }) => (
              <FormItem>
                <FormLabel>District</FormLabel>
                <Select
                  onValueChange={(value) =>
                    field.onChange(handleDistrictChange(value))
                  }
                  value={field.value}
                  disabled={isLoadingDistricts}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a district" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {districts?.map((district) => (
                      <SelectItem key={district.id} value={district.name}>
                        {district.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Hidden fields */}
          <FormField
            control={form.control}
            name="city"
            render={({ field }) => <input type="hidden" {...field} />}
          />

          <FormField
            control={form.control}
            name="overwriteId"
            render={({ field }) => <input type="hidden" {...field} />}
          />

          {!hideSubmit && (
            <div className="flex items-center pt-2">
              <Button
                type="submit"
                disabled={form.formState.isSubmitting || isLoadingDistricts}
                className="w-full sm:w-auto"
              >
                {form.formState.isSubmitting ? (
                  <Indicators.Spinner size="sm" className="mr-2" />
                ) : null}
                Add
              </Button>
            </div>
          )}
        </form>
      </Form>
    </div>
  )
}
